import React, { useState } from 'react';

import { Button, ButtonProps, Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { ApiPersonDetailReviewsListServiceTypeCategory as ServiceTypeCategories } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListServiceTypeCategory';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

import { TrainerProfileReviewsFilterChanged } from '../../events/analyticsEvents';

const mapTypeToText = {
  [ServiceTypeCategories['dog-training']]: 'Training only',
  [ServiceTypeCategories['all-services']]: 'All services',
};

type Props = {
  onChange: (serviceTypeCategory: ServiceTypeCategories) => void;
  initialServiceTypeCategory?: ServiceTypeCategories;
};

const getCommonButtonStyle = (theme: RoverTheme): ButtonProps['sx'] => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.radii.secondary,
  maxHeight: '34px',
  paddingY: `${theme.space['2x']} !important`,
  paddingX: `${theme.space['3x']} !important`,
  '&:hover': {
    backgroundColor: theme.background.tertiary,
  },
  '&:focus': {
    backgroundColor: theme.background.accent_secondary,
  },
  width: ['100%', 'auto'],
  margin: '2px',
});

const getSelectedButtonStyle = (theme: RoverTheme): ButtonProps['sx'] => ({
  ...getCommonButtonStyle(theme),
  boxShadow: `0px 0px 0px 2px ${theme.colors.interactive_text.primary_active}`,
  backgroundColor: theme.background.accent_secondary,
});

const ServiceTypeReviewFilter = ({ onChange, initialServiceTypeCategory }: Props): JSX.Element => {
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<ServiceTypeCategories>(
    initialServiceTypeCategory ?? ServiceTypeCategories['dog-training']
  );

  const filterButtons = Object.keys(ServiceTypeCategories).map((key, idx) => {
    if (key === ServiceTypeCategories['core-services']) {
      return null;
    }

    const category = ServiceTypeCategories[key];
    const isSelected = selectedCategory === category;
    const style = isSelected ? getSelectedButtonStyle(theme) : getCommonButtonStyle(theme);
    const text = mapTypeToText[key];
    const onClick = (): void => {
      setSelectedCategory(category);
      onChange(category);
      emitAnalyticsEvent(new TrainerProfileReviewsFilterChanged({ serviceTypeCategory: category }));
    };

    return (
      <Button key={`${text}-${idx}`} sx={style} onClick={onClick}>
        <Text size="100" fontWeight="semibold" sx={{ whiteSpace: 'nowrap' }}>
          {text}
        </Text>
      </Button>
    );
  });

  return <Flex gap="2x">{filterButtons}</Flex>;
};

export default ServiceTypeReviewFilter;
